import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import * as Flags from 'country-flag-icons/react/3x2';

import { useSelector } from 'hooks';
import { isLocalKiosk } from 'modules/dealers/selectors';
import { Country } from 'modules/refData/types/Countries';

import Spinner from '../Spinner';
import useKeybindings from './hooks/useKeybindings';

export interface CountryListContentProps {
  close: () => void;
  countries: Country[];
  onClick: (country: Country, close: () => void) => () => void;
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  searchText: string;
  setSearchText: (text: string) => void;
  value?: string;
}

const CountryListContent: React.FC<CountryListContentProps> = ({
  close,
  countries,
  onClick,
  onSearch,
  isLoading,
  searchText,
  value,
}) => {
  const intl = useIntl();
  const isKiosk = useSelector(isLocalKiosk);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(undefined);

  const setInputRef = React.useCallback((element: HTMLInputElement | null) => {
    inputRef.current = element;
  }, []);

  React.useEffect(() => {
    if (!isKiosk) {
      inputRef.current?.focus();
    }
  }, [isKiosk]);

  React.useEffect(() => {
    setSelectedIndex(undefined);
  }, [countries]);

  React.useEffect(() => {
    if (listRef.current && typeof selectedIndex === 'number') {
      const highlightedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleKeyDown = useKeybindings({
    selectedIndex: selectedIndex ?? -1,
    onClose: close,
    onClick: onClick,
    onChange: setSelectedIndex,
    countries: countries,
  });

  const handleMouseEnter = React.useCallback((event: React.MouseEvent<HTMLLIElement>) => {
    const index = Number(event.currentTarget.dataset.index);
    setSelectedIndex(index);
  }, []);

  const handleOptionClick = React.useCallback((event: React.MouseEvent<HTMLLIElement>) => {
    const index = Number(event.currentTarget.dataset.index);
    const country = countries[index];
    if (country) {
      onClick(country, close)();
    }
  }, [countries, onClick, close]);

  const selectedCountry = typeof selectedIndex === 'number' ? countries[selectedIndex] : undefined;

  return (
    <div className="h-full flex flex-col bg-white pb-4">
      <div className="relative w-full shrink-0 p-4">
        <input
          ref={setInputRef}
          value={searchText}
          onChange={onSearch}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({ id: 'customerInfo.prefixPlaceholder', defaultMessage: 'Search by prefix or country' })}
          className="w-full rounded-lg border-2 focus:outline-hidden py-4 px-6 font-normal placeholder:text-neutral-600 bg-white border-neutral-100 kiosk:placeholder:text-lg"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={true}
          aria-controls="country-listbox"
          aria-activedescendant={
            selectedCountry
              ? `country-option-${selectedCountry.code}`
              : undefined
          }
        />
        <span className="fb-icon--search absolute kiosk:right-8 right-4 kiosk:mr-0 mr-4 top-1/2 -translate-y-1/2 text-2xl kiosk:text-3xl" />
      </div>
      {isLoading && <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>}
      {!isLoading && (
        <ul
          role="listbox"
          id="country-listbox"
          ref={listRef}
          className="flex-1 overflow-y-auto"
        >
          {countries.map((country, index) => {
            const Flag = Flags[country.code as keyof typeof Flags];
            const isHighlighted = index === selectedIndex;
            const isSelected = country.code === value;
            return (
              <li
                key={country.code}
                id={`country-option-${country.code}`}
                role="option"
                aria-selected={isHighlighted || isSelected}
                data-index={index}
                onClick={handleOptionClick}
                onMouseEnter={handleMouseEnter}
                className={classNames('flex items-center h-8 kiosk:h-12 px-6 gap-2 kiosk:gap-4 cursor-pointer', {
                  'bg-neutral-100': isHighlighted || isSelected,
                })}
              >
                <Flag className="w-8 h-8" />
                <span className="flex-1 truncate text-neutral-900">{country.name}</span>
                <span className="text-neutral-600">+{country.prefix}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CountryListContent;